/**
 * Create / edit form for an itinerary review (U6). D2 shape: one scalar 1–5
 * rating plus an OPTIONAL body with no minimum length — deliberately not the
 * five-subscore `VenueReviewForm`.
 *
 * Repo form conventions: a `<div>` (never a `<form>`), action buttons at the
 * TOP RIGHT, submission via `onClick`, errors surfaced beside those buttons.
 */

import { useState } from 'react';
import { StarRating } from '../reviews/StarRating';
import { Button } from '../ui/Button';
import { Heading3, Caption } from '../ui/Typography';

const BODY_MAX_LENGTH = 20000;

export interface ItineraryReviewDraft {
  rating: number;
  body: string;
}

interface AddItineraryReviewProps {
  /** Heading above the form — differs between "write" and "edit". */
  heading: string;
  initialRating?: number;
  initialBody?: string;
  submitLabel: string;
  onSubmit: (draft: ItineraryReviewDraft) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Server-side error to show beside the action buttons. */
  submitError?: string | null;
}

export const AddItineraryReview = ({
  heading,
  initialRating = 0,
  initialBody = '',
  submitLabel,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitError = null,
}: AddItineraryReviewProps) => {
  const [rating, setRating] = useState(initialRating);
  const [body, setBody] = useState(initialBody);
  const [ratingError, setRatingError] = useState<string | null>(null);

  const handleRatingChange = (value: number) => {
    setRating(value);
    setRatingError(null);
  };

  const handleSubmit = () => {
    if (rating < 1 || rating > 5) {
      setRatingError('Choose a rating from 1 to 5 stars.');
      return;
    }
    onSubmit({ rating, body: body.trim() });
  };

  return (
    <div className="rounded-lg border border-[#DBD4C9] bg-[#F2F0EA] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Heading3 className="text-gray-900">{heading}</Heading3>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      {(ratingError || submitError) && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {ratingError ?? submitError}
        </p>
      )}

      <StarRating
        className="mt-4"
        value={rating}
        onChange={handleRatingChange}
        label="Your rating"
        required
        disabled={isSubmitting}
        error={ratingError ?? undefined}
      />

      <div className="mt-4">
        <div className="mb-1 flex items-center justify-between">
          <label
            htmlFor="itinerary-review-body"
            className="block text-sm font-medium text-gray-700"
          >
            Your review <span className="text-gray-500">(optional)</span>
          </label>
          <Caption className="text-gray-500">
            {body.length}/{BODY_MAX_LENGTH}
          </Caption>
        </div>
        <textarea
          id="itinerary-review-body"
          rows={4}
          value={body}
          disabled={isSubmitting}
          maxLength={BODY_MAX_LENGTH}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What was this trip like to follow?"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
        />
      </div>
    </div>
  );
};
